import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import CustomButton from "../Common/CustomButton";
import useFetch from "../Common/useFetch";
import ActionConfirmationModal from "../Vendor/ActionConfirmationModal";
import { CN_Status_Type } from "../VendorNew/VendorSideDashboard";
import { EarlyPaymentStatus, IYesNo } from "./AnchorDashboard";

const BulkOperationsModal = ({
  open,
  onClose,
  selectedRowIds,
  clearSelectedRows,
  useCase,
  getAllData,
  onCloseParent,
}: {
  open: boolean;
  onClose: () => void;
  selectedRowIds: string[];
  clearSelectedRows: () => void;
  useCase: "admin" | "anchor";
  getAllData: () => void;
  onCloseParent?: () => void;
}) => {
  const [openConfirmationModal, setOpenConfirmationModal] = useState<boolean>(false);
  const [selectedFields, setSelectedFields] = useState<{
    actualCNReceivedNakad: boolean;
    actualCNReceivedAnchor: boolean;
    earlyPaymentStatus: boolean;
    dnAdjusted: boolean;
    cnStatus: boolean;
    earlyPayDate: boolean;
    earlyPaidAmount: boolean;
  }>({
    actualCNReceivedNakad: false,
    actualCNReceivedAnchor: false,
    earlyPaymentStatus: false,
    dnAdjusted: false,
    cnStatus: false,
    earlyPayDate: false,
    earlyPaidAmount: false,
  });

  const [fieldValues, setFieldValues] = useState<{
    actualCNReceivedNakad: IYesNo;
    actualCNReceivedAnchor: IYesNo;
    earlyPaymentStatus: EarlyPaymentStatus;
    dnAdjusted: IYesNo;
    cnStatus: CN_Status_Type;
    earlyPayDate: string;
    earlyPaidAmount: string;
  }>({
    actualCNReceivedNakad: IYesNo.NO,
    actualCNReceivedAnchor: IYesNo.NO,
    earlyPaymentStatus: EarlyPaymentStatus.YET_TO_BE_INITIATED,
    dnAdjusted: IYesNo.NO,
    cnStatus: CN_Status_Type.CN_PENDING,
    earlyPayDate: "",
    earlyPaidAmount: "",
  });

  const handleBulkAction = async () => {
    const selectedValues = {};

    Object.keys(selectedFields).forEach((field) => {
      if (selectedFields[field]) {
        selectedValues[field] = fieldValues[field];
      }
    });

    await useFetch("/invoices/previous-payment", "PUT", {
      failureMessage: "Failed to perform bulk action",
      data: {
        ids: selectedRowIds,
        anchorCnReceived: selectedFields.actualCNReceivedAnchor ? fieldValues.actualCNReceivedAnchor : undefined,
        nakadCnReceived:
          useCase === "admin" && selectedFields.actualCNReceivedNakad ? fieldValues.actualCNReceivedNakad : undefined,
        earlyPaymentStatus: selectedFields.earlyPaymentStatus ? fieldValues.earlyPaymentStatus : undefined,
        dnAdjusted: selectedFields.dnAdjusted ? fieldValues.dnAdjusted : undefined,
        cnStatus: selectedFields.cnStatus ? fieldValues.cnStatus : undefined,
        earlyPayDate: selectedFields.earlyPayDate ? fieldValues.earlyPayDate : undefined,
        earlyPaidAmount: selectedFields.earlyPaidAmount ? fieldValues.earlyPaidAmount : undefined,
      },
      thenCallBack: () => {
        clearSelectedRows();
        if (onCloseParent) onCloseParent();
        getAllData();
        onClose();
        setOpenConfirmationModal(false);
      },
    });
  };

  const handleCheckboxChange = (field) => {
    setSelectedFields((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));

    // Reset value when unchecked
    if (selectedFields[field]) {
      setFieldValues((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSelectChange = (field, value) => {
    setFieldValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCancel = () => {
    // Reset all states
    setSelectedFields({
      actualCNReceivedNakad: false,
      actualCNReceivedAnchor: false,
      earlyPaymentStatus: false,
      dnAdjusted: false,
      cnStatus: false,
      earlyPayDate: false,
      earlyPaidAmount: false,
    });
    setFieldValues({
      actualCNReceivedNakad: IYesNo.NO,
      actualCNReceivedAnchor: IYesNo.NO,
      earlyPaymentStatus: EarlyPaymentStatus.YET_TO_BE_INITIATED,
      dnAdjusted: IYesNo.NO,
      cnStatus: CN_Status_Type.CN_PENDING,
      earlyPayDate: "",
      earlyPaidAmount: "",
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* Header */}
      <DialogTitle
        sx={{
          backgroundColor: "#f5f5f5",
          padding: "8px 24px",
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" component="h2">
          Bulk Actions
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="body1" mt={1}>
          {selectedRowIds.length} Invoices selected
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <Checkbox checked={selectedFields.cnStatus} onChange={() => handleCheckboxChange("cnStatus")} />
          <FormControl fullWidth>
            <InputLabel id="cn-status-label">CN Status</InputLabel>
            <Select
              labelId="cn-status-label"
              id="cn-status"
              size="small"
              label="CN Status"
              value={fieldValues.cnStatus}
              onChange={(e) => handleSelectChange("cnStatus", e.target.value)}
              disabled={!selectedFields.cnStatus}
              displayEmpty
            >
              <MenuItem value={CN_Status_Type.CN_PENDING}>{CN_Status_Type.CN_PENDING}</MenuItem>
              <MenuItem value={CN_Status_Type.CN_VERIFIED}>{CN_Status_Type.CN_VERIFIED}</MenuItem>
              <MenuItem value={CN_Status_Type.CN_REJECTED}>{CN_Status_Type.CN_REJECTED}</MenuItem>
              <MenuItem value={CN_Status_Type.REVIEW_PENDING}>{CN_Status_Type.REVIEW_PENDING}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Actual CN Received */}
        {useCase === "admin" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <Checkbox
              checked={selectedFields.actualCNReceivedNakad}
              onChange={() => handleCheckboxChange("actualCNReceivedNakad")}
            />
            <FormControl fullWidth>
              <InputLabel id="actual-cn-received-nakad-label">Actual CN received (Nakad)</InputLabel>
              <Select
                labelId="actual-cn-received-nakad-label"
                id="actual-cn-received-nakad"
                label="Actual CN received (Nakad)"
                size="small"
                value={fieldValues.actualCNReceivedNakad}
                onChange={(e) => handleSelectChange("actualCNReceivedNakad", e.target.value)}
                disabled={!selectedFields.actualCNReceivedNakad}
                displayEmpty
              >
                <MenuItem value={IYesNo.YES}>{IYesNo.YES}</MenuItem>
                <MenuItem value={IYesNo.NO}>{IYesNo.NO}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <Checkbox
            checked={selectedFields.actualCNReceivedAnchor}
            onChange={() => handleCheckboxChange("actualCNReceivedAnchor")}
          />
          <FormControl fullWidth>
            <InputLabel id="actual-cn-received-anchor-label">Actual CN received (Swiggy)</InputLabel>
            <Select
              labelId="actual-cn-received-anchor-label"
              id="actual-cn-received-anchor"
              size="small"
              label="Actual CN received (Swiggy)"
              value={fieldValues.actualCNReceivedAnchor}
              onChange={(e) => handleSelectChange("actualCNReceivedAnchor", e.target.value)}
              disabled={!selectedFields.actualCNReceivedAnchor}
              displayEmpty
            >
              <MenuItem value={IYesNo.YES}>{IYesNo.YES}</MenuItem>
              <MenuItem value={IYesNo.NO}>{IYesNo.NO}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Early Payment Status */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <Checkbox
            checked={selectedFields.earlyPaymentStatus}
            onChange={() => handleCheckboxChange("earlyPaymentStatus")}
          />
          <FormControl sx={{ flex: 1 }}>
            <InputLabel id="early-payment-status-label">Early Payment Status</InputLabel>
            <Select
              labelId="early-payment-status-label"
              id="early-payment-status-select"
              label="Early Payment Status"
              size="small"
              value={fieldValues.earlyPaymentStatus}
              onChange={(e) => handleSelectChange("earlyPaymentStatus", e.target.value)}
              disabled={!selectedFields.earlyPaymentStatus}
              IconComponent={KeyboardArrowDownIcon}
              displayEmpty
            >
              <MenuItem value={EarlyPaymentStatus.YET_TO_BE_INITIATED}>
                {EarlyPaymentStatus.YET_TO_BE_INITIATED}
              </MenuItem>
              <MenuItem value={EarlyPaymentStatus.UNDER_PROCESSING}>{EarlyPaymentStatus.UNDER_PROCESSING}</MenuItem>
              <MenuItem value={EarlyPaymentStatus.PROCESSED}>{EarlyPaymentStatus.PROCESSED}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* DN Adjusted */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <Checkbox checked={selectedFields.dnAdjusted} onChange={() => handleCheckboxChange("dnAdjusted")} />
          <FormControl sx={{ flex: 1 }}>
            <InputLabel id="dn-adjusted-label">DN Adjusted</InputLabel>
            <Select
              labelId="dn-adjusted-label"
              id="dn-adjusted-select"
              size="small"
              label="DN Adjusted"
              value={fieldValues.dnAdjusted}
              onChange={(e) => handleSelectChange("dnAdjusted", e.target.value)}
              disabled={!selectedFields.dnAdjusted}
              displayEmpty
              IconComponent={KeyboardArrowDownIcon}
            >
              <MenuItem value={IYesNo.YES}>{IYesNo.YES}</MenuItem>
              <MenuItem value={IYesNo.NO}>{IYesNo.NO}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Early Pay Date */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <Checkbox checked={selectedFields.earlyPayDate} onChange={() => handleCheckboxChange("earlyPayDate")} />
          <TextField
            fullWidth
            size="small"
            label="Early Pay Date"
            type="date"
            value={fieldValues.earlyPayDate}
            onChange={(e) => handleSelectChange("earlyPayDate", e.target.value)}
            disabled={!selectedFields.earlyPayDate}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {/* Early Paid Amount */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <Checkbox checked={selectedFields.earlyPaidAmount} onChange={() => handleCheckboxChange("earlyPaidAmount")} />
          <TextField
            fullWidth
            size="small"
            label="Early Paid Amount"
            type="number"
            value={fieldValues.earlyPaidAmount}
            onChange={(e) => handleSelectChange("earlyPaidAmount", e.target.value)}
            disabled={!selectedFields.earlyPaidAmount}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          padding: "16px 24px",
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
        }}
      >
        <CustomButton variant="text" onClick={handleCancel}>
          Cancel
        </CustomButton>
        <CustomButton
          variant="contained"
          onClick={() => setOpenConfirmationModal(true)}
          disabled={!Object.values(selectedFields).some(Boolean)}
        >
          Update
        </CustomButton>
      </DialogActions>
      {openConfirmationModal && (
        <ActionConfirmationModal
          open={openConfirmationModal}
          onClose={() => setOpenConfirmationModal(false)}
          onConfirm={handleBulkAction}
          title="Are you sure?"
          description={`All ${selectedRowIds.length} bulk actions will be performed and cannot be reverted.`}
        />
      )}
    </Dialog>
  );
};

export default BulkOperationsModal;
